import * as Observability from '@code-dot-org/core/plugins/observability';

import DCDO from '@cdo/apps/dcdo';
import {getBrowserName} from '@cdo/apps/util/browser-detector';
import {isDevelopmentEnvironment} from '@cdo/apps/utils';

import DashboardMetricsApi from './DashboardMetricsApi';
import {MetricsApi} from './MetricsApi';
import {LogLevel, MetricDatum, MetricDimension, MetricUnit} from './types';

/**
 * If we receive an unauthorized response from the server, this may
 * indicate that browser event reporting has been temporarily disabled.
 * We will wait for a specific time interval (defined below) before making
 * a request again, so as to not flood the server with requests.
 */
const CHECK_CAN_REPORT_INTERVAL_MINUTES = 30;
const CHECK_CAN_REPORT_INTERVAL_MS =
  CHECK_CAN_REPORT_INTERVAL_MINUTES * 60 * 1000;
const LOCAL_STORAGE_KEY_NAME = 'cdo-metrics-reporter-last-check-time';
// A flag that can be toggled to send events regardless of environment
const ALWAYS_SEND = false;

const observabilityLoggerByLevel: Record<
  LogLevel,
  (message: string, context: Record<string, unknown>) => void
> = {
  INFO: (message, context) => Observability.logger.info(message, context),
  WARNING: (message, context) => Observability.logger.warn(message, context),
  SEVERE: (message, context) => Observability.logger.error(message, context),
};

/**
 * Reports logs and metrics, intended primarily for developer-facing
 * error reporting, metric reporting, and logging.
 *
 * For tracking user interactions and behaviors (product-facing),
 * see {@link AnalyticsReporter} which reports to Amplitude.
 *
 * For legacy client-side reporting see {@link firehose} for AWS
 * Firehose reporting and {@link logToCloud} as a backwards-compatible
 * facade for browser observability.
 */
class MetricsReporter {
  private lastCheckCanReportTime: number;

  constructor(private readonly metricsApi: MetricsApi) {
    this.metricsApi = metricsApi;
    this.lastCheckCanReportTime =
      parseInt(localStorage.getItem(LOCAL_STORAGE_KEY_NAME) || '0') || 0;
  }

  /**
   * Publish an information log message. Can be a string or a structured object
   */
  logInfo(message: string | object) {
    if (!this.shouldReport()) {
      console.log(message);
      return;
    }
    this.log('INFO', message);
  }

  /**
   * Publish a warning log message. Can be a string or a structured object
   */
  logWarning(message: string | object) {
    if (!this.shouldReport()) {
      console.warn(message);
      return;
    }
    this.log('WARNING', message);
  }

  /**
   * Publish an error log message. Can be a string or a structured object
   */
  logError(message: string | object) {
    if (!this.shouldReport()) {
      console.error(message);
      return;
    }
    this.log('SEVERE', message);
  }

  /**
   * Increment a counter metric.
   */
  incrementCounter(name: string, dimensions: MetricDimension[] = []) {
    this.publishMetric(name, 1, 'Count', dimensions);
  }

  /**
   * Publish a metric.
   *
   * Note that this will send two metrics, with and without the browser version dimension
   * (the browser name dimension is included in both).
   * This allows us to more easily aggregate metrics by browser.
   */
  publishMetric(
    name: string,
    value: number,
    unit: MetricUnit,
    dimensions: MetricDimension[] = []
  ) {
    const metric = {
      name,
      value,
      unit,
      dimensions: dimensions.concat(this.getDeviceDimensions()),
    };
    if (!this.shouldReport()) {
      console.info('[MetricsReporter] ' + JSON.stringify(metric));
      return;
    }

    if (DCDO.get('frontend-observability-enabled', false)) {
      const dimensionAttributes = Object.fromEntries(
        metric.dimensions
          .filter(d => d?.name && d?.value)
          .map(d => [d.name, d.value])
      );
      Observability.metrics.count(name, value, {unit, ...dimensionAttributes});
    }

    // Send a version of the metric with and without the browser version dimension
    this.sendMetrics([
      metric,
      {
        ...metric,
        dimensions: [...metric.dimensions, this.getBrowserVersionDimension()],
      },
    ]);
  }

  private async log(level: LogLevel, message: string | object) {
    const payload = {
      level,
      message,
      deviceInfo: this.getDeviceInfo(),
    };

    if (DCDO.get('frontend-observability-enabled', false)) {
      this.sendToObservabilityLogger(level, message);
    }

    if (!this.isReportingEnabled()) {
      this.fallbackLog(payload);
      return;
    }

    if (!DCDO.get('browser-events-enabled', true)) {
      return;
    }

    try {
      await this.metricsApi.sendLogs([payload]);
    } catch (error) {
      this.fallbackLog(payload);
      this.handleError(error as Error);
    }
  }

  private sendToObservabilityLogger(level: LogLevel, message: string | object) {
    // TODO: Refactor all log entrypoints (logInfo, logWarning, logError) to
    // accept explicit (msg: string, context: object) signatures once we fully
    // migrate to publishing directly to the log provider. Until then, we feel
    // for known string fields to use as the Sentry log message.
    if (typeof message === 'string') {
      observabilityLoggerByLevel[level](message, this.getDeviceInfo());
    } else {
      const {
        message: msgField,
        errorMessage,
        ...rest
      } = message as Record<string, unknown>;
      let msgStr: string;
      if (typeof msgField === 'string') {
        msgStr = msgField;
      } else if (typeof errorMessage === 'string') {
        msgStr = errorMessage;
      } else {
        msgStr = level;
      }
      observabilityLoggerByLevel[level](msgStr, {
        ...this.getDeviceInfo(),
        ...rest,
      });
    }
  }

  private async sendMetrics(metrics: MetricDatum[]) {
    if (!this.isReportingEnabled()) {
      this.fallbackLog(metrics);
      return;
    }

    if (!DCDO.get('browser-events-enabled', true)) {
      return;
    }

    try {
      await this.metricsApi.sendMetricData(metrics);
    } catch (error) {
      this.fallbackLog(metrics);
      this.handleError(error as Error);
    }
  }

  private handleError(error: Error) {
    if (error.message.includes('401')) {
      // Unauthorized response from server; client logging is likely disabled.
      // We will check again after a time period of CHECK_CAN_REPORT_INTERVAL
      this.setReportingDisabled();
    } else {
      console.error(error);
    }
  }

  private getDeviceInfo(): Record<string, unknown> {
    return {
      user_agent: window.navigator.userAgent,
      window_width: window.innerWidth,
      window_height: window.innerHeight,
      hostname: window.location.hostname,
      full_path: window.location.href,
    };
  }

  private getDeviceDimensions(): MetricDimension[] {
    return [
      {
        name: 'Hostname',
        value: window.location.hostname,
      },
      {
        name: 'Browser',
        value: getBrowserName(),
      },
    ];
  }

  private getBrowserVersionDimension(): MetricDimension {
    return {
      name: 'BrowserVersion',
      value: getBrowserName(true),
    };
  }

  private fallbackLog(payload: object) {
    if (isDevelopmentEnvironment()) {
      console.log(
        'Client-side reporting disabled. Attempted to report: ' +
          JSON.stringify(payload)
      );
    }
  }

  private isReportingEnabled(): boolean {
    return (
      Date.now() - this.lastCheckCanReportTime > CHECK_CAN_REPORT_INTERVAL_MS
    );
  }

  private setReportingDisabled() {
    this.lastCheckCanReportTime = Date.now();
    localStorage.setItem(
      LOCAL_STORAGE_KEY_NAME,
      this.lastCheckCanReportTime.toString()
    );
  }

  /**
   * Whether we should try to report metrics to the server.
   * Always true if not on development. If on development,
   * this is controlled by the ALWAYS_SEND flag.
   */
  private shouldReport(): boolean {
    return ALWAYS_SEND || !isDevelopmentEnvironment();
  }
}

export default new MetricsReporter(new DashboardMetricsApi());
